import type { Rng } from './rng.js';

/**
 * Bridson's Poisson disc sampling — gives evenly-spaced points
 * which produce far more uniform Voronoi cells than uniform random sampling.
 */
export function poissonDiscSample(
  width: number,
  height: number,
  minDistance: number,
  rng: Rng,
  k = 30,
): Array<[number, number]> {
  const cellSize = minDistance / Math.SQRT2;
  const gridW = Math.ceil(width / cellSize);
  const gridH = Math.ceil(height / cellSize);
  const grid: Array<[number, number] | null> = new Array(gridW * gridH).fill(null);

  const points: Array<[number, number]> = [];
  const active: Array<[number, number]> = [];

  const insert = (p: [number, number]) => {
    points.push(p);
    active.push(p);
    const gx = Math.floor(p[0] / cellSize);
    const gy = Math.floor(p[1] / cellSize);
    grid[gy * gridW + gx] = p;
  };

  insert([rng() * width, rng() * height]);

  while (active.length > 0) {
    const idx = (rng() * active.length) | 0;
    const base = active[idx]!;
    let placed = false;

    for (let i = 0; i < k; i++) {
      const angle = rng() * Math.PI * 2;
      const radius = minDistance * (1 + rng());
      const x = base[0] + Math.cos(angle) * radius;
      const y = base[1] + Math.sin(angle) * radius;

      if (x < 0 || x >= width || y < 0 || y >= height) continue;

      const gx = Math.floor(x / cellSize);
      const gy = Math.floor(y / cellSize);
      let ok = true;
      const minD2 = minDistance * minDistance;
      for (let dy = -2; dy <= 2 && ok; dy++) {
        for (let dx = -2; dx <= 2 && ok; dx++) {
          const nx = gx + dx;
          const ny = gy + dy;
          if (nx < 0 || ny < 0 || nx >= gridW || ny >= gridH) continue;
          const n = grid[ny * gridW + nx];
          if (!n) continue;
          const ex = n[0] - x;
          const ey = n[1] - y;
          if (ex * ex + ey * ey < minD2) ok = false;
        }
      }
      if (ok) {
        insert([x, y]);
        placed = true;
        break;
      }
    }
    if (!placed) {
      active.splice(idx, 1);
    }
  }

  return points;
}
